import { forwardRef } from "react";
import { HiChevronDown } from "react-icons/hi";

import { Avatar } from "MVVM/Components/AvatarV2";
import { UserTierText } from "MVVM/Components/SiteHeader/UserCard";
import { TUserGroup, TUserRank } from "MVVM/@types";
import { useUser } from "MVVM/Hooks/useUser";
import { OverallState } from "MVVM/Components/ClipStatus/@types";
import { useActiveClips } from "./useActiveClips";
import type { useSetupChecklist } from "MVVM/Hooks/useSetupChecklist";
import {
  AvatarDot,
  AvatarSlot,
  PillCaret,
  PillLabel,
  PillStatus,
  PillTier,
  PillTrigger,
  PillUsername,
  SetupLine,
  SetupMiniMeter,
  SetupMiniSegment,
  SetupProgress,
} from "./styledComponents";

/**
 * Live state for the trigger, read from the atom ClipStatusTray already keeps
 * populated app-wide, so this costs no extra request.
 *
 * The design also wants an "N new clips ready" rung. There is no unseen or
 * last-seen concept anywhere (ViewedClips only records clips you opened), so
 * that one waits on a lastSeenAt / clipsSince API.
 */
const useLiveStatus = () => {
  const active = useActiveClips();

  if (!active.length) return null;

  const montages = active.filter((entry) => entry.montageId);
  if (montages.length) return "Montage rendering…";

  const started = active.some(
    (entry) => entry.overallState === OverallState.IN_PROGRESS,
  );
  return started
    ? `Clipping ${active.length > 1 ? `${active.length} plays` : "your play"}…`
    : "Clip queued…";
};

export const StatusPill = forwardRef<
  HTMLButtonElement,
  {
    open: boolean;
    panelId: string;
    checklist: ReturnType<typeof useSetupChecklist>;
    onClick: () => void;
  }
>(({ open, panelId, checklist, onClick }, ref) => {
  const { allstarUser } = useUser();
  const status = useLiveStatus();
  const user = allstarUser?.user;
  const username = user?.profile?.username || user?.username || "";
  const { showIndicator: setupPending, done, total } = checklist;
  const setupLabel = setupPending ? `, ${total - done} setup steps left` : "";

  return (
    <PillTrigger
      ref={ref}
      type="button"
      $open={open}
      aria-expanded={open}
      aria-controls={panelId}
      aria-label={`Account menu${status ? `, ${status}` : ""}${setupLabel}`}
      onClick={onClick}
    >
      <AvatarSlot>
        <Avatar
          src={allstarUser?.myAvatar}
          size="sm"
          userGroup={user?.userGroup?.type as TUserGroup}
          rank={user?.rank as TUserRank}
        />
        {setupPending && <AvatarDot aria-hidden="true" />}
      </AvatarSlot>
      <PillLabel>
        <PillUsername>{username}</PillUsername>
        {/* Second line: tier normally, render progress while there is any. The
            string changes under the user, hence aria-live. The panel used to
            repeat the avatar and name to carry the tier; the trigger is the
            panel's header now, so it lives here instead. */}
        {status ? (
          <PillStatus aria-live="polite">{status}</PillStatus>
        ) : setupPending ? (
          <SetupLine>
            <SetupMiniMeter aria-hidden="true">
              {Array.from({ length: total }, (_, index) => (
                <SetupMiniSegment key={index} $filled={index < done} />
              ))}
            </SetupMiniMeter>
            Setup <SetupProgress>{total - done} left</SetupProgress>
          </SetupLine>
        ) : (
          <PillTier>
            <UserTierText tier={user?.userGroup?.type || "FREE"} />
          </PillTier>
        )}
      </PillLabel>
      <PillCaret $open={open} aria-hidden="true">
        <HiChevronDown />
      </PillCaret>
    </PillTrigger>
  );
});

StatusPill.displayName = "StatusPill";
